import type { ReactNode } from 'react'

import { cn } from '@/lib/cn'

export interface EmptyStateProps {
  title: string
  /**
   * Heading level. `h3` suits a region inside a page that already has an h1;
   * pass `h1` where this empty state *is* the page, as on the 404 route.
   */
  titleAs?: 'h1' | 'h2' | 'h3'
  description?: ReactNode
  icon?: ReactNode
  actions?: ReactNode
  /** `compact` for an empty region inside a card; `full` for a whole page. */
  size?: 'compact' | 'full'
  className?: string
}

/**
 * Nothing to show, said usefully.
 *
 * An empty state that only says "no results" wastes the one moment the reader
 * is definitely paying attention, so every one of these carries a next step.
 */
export function EmptyState({
  title,
  titleAs: Heading = 'h3',
  description,
  icon,
  actions,
  size = 'full',
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center px-6 text-center',
        size === 'full' ? 'py-16' : 'py-10',
        className,
      )}
    >
      {icon ? (
        <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-surface-sunken text-ink-muted">
          {icon}
        </div>
      ) : null}
      <Heading className="text-base font-semibold text-ink">{title}</Heading>
      {description ? (
        <p className="mt-1 max-w-md text-sm text-ink-secondary">{description}</p>
      ) : null}
      {actions ? (
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          {actions}
        </div>
      ) : null}
    </div>
  )
}
